import { ArrowLeft } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'

import { useSendAffiliateEmail } from '@/api/affiliate/mutations/useSendAffiliateEmail'
import { useAffiliateUserData } from '@/api/affiliate/queries/useAffiliateUserData'
import { Payment, PaymentTypeEnum } from '@/api/affiliate/types/AffUser'
import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { AppDialog } from '@/components/AppDialog'
import CouponIcon from '@/components/icons/coupon.svg?react'
import GiftCardIcon from '@/components/icons/gift-card.svg?react'
import LinkIcon from '@/components/icons/link.svg?react'
import ProfitIcon from '@/components/icons/profit.svg?react'
import UsersIcon from '@/components/icons/users.svg?react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'

import { CopyTextButton } from '../../components/CopyTextButton'
import { AffiliateCouponSection } from './AffiliateCouponSection'

const paymentTypeMap = {
  [PaymentTypeEnum.Shipments]: { label: 'לינק', Icon: LinkIcon, color: '#E3F4FC', iconColor: '#1EB4FF' },
  [PaymentTypeEnum.Coupon]: { label: 'קופון', Icon: CouponIcon, color: '#FFF7DF', iconColor: '#FAD600' },
  [PaymentTypeEnum.Giftcard]: { label: 'גיפט קארד', Icon: GiftCardIcon, color: '#F2F0EC', iconColor: '#A98F5B' },
}
interface PaymentDetailsDialogProps {
  paymentType: PaymentTypeEnum
  payments: Payment[]
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const PaymentDetailsDialog = ({ paymentType, payments, isOpen, onOpenChange }: PaymentDetailsDialogProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('he-IL')
  }

  const title = paymentTypeMap[paymentType].label
  const iconColor = paymentTypeMap[paymentType].iconColor
  const Icon = paymentTypeMap[paymentType].Icon

  return (
    <AppDialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className='mb-6 flex flex-col items-center justify-center gap-4'>
        <Icon className='size-6' style={{ color: iconColor }} />
        <div className='text-text-primary text-lg font-medium'>{title}</div>
      </div>
      <div>
        <div className='bg-background-secondary border-border-default grid grid-cols-2 gap-4 border-b p-3'>
          <div className='text-text-secondary text-end text-sm font-medium'>תאריך</div>
          <div className='text-text-secondary text-end text-sm font-medium'>סכום</div>
        </div>
        {payments.map((payment: Payment, idx: number) => (
          <div key={idx} className='border-border-default grid grid-cols-2 gap-4 border-b p-3'>
            <div className='text-text-primary text-end text-sm'>{formatDate(payment.date)}</div>
            <div className='text-text-primary text-end text-sm'>{payment.payment}₪</div>
          </div>
        ))}
      </div>
    </AppDialog>
  )
}

export default function ProfileAffiliatePage() {
  const { data: currentUser } = useCurrentUser()
  const refNum = currentUser?.UserAttributes?.['custom:refNum']
  const { data: refUserData, isLoading: isLoadingAffiliateUserData } = useAffiliateUserData()
  const coupons = refUserData?.coupons
  const { mutate: sendAffiliateEmail, isPending: isSendingEmail } = useSendAffiliateEmail()

  const [isAffiliateModalOpen, setIsAffiliateModalOpen] = useState(false)
  const [affiliateModalMessage, setAffiliateModalMessage] = useState('')
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentTypeEnum | null>(null)
  const formAboutRef = useRef<HTMLTextAreaElement>(null)
  const formLinksRef = useRef<HTMLTextAreaElement>(null)

  const handleAffiliateSubmit = async () => {
    if (!formAboutRef.current?.value) return
    const user = currentUser?.UserAttributes
    if (!user) return

    const data = `name: ${user.name} ${user.family_name}. phone: ${user.phone_number}. email: ${user.email}.
    ספרי לנו על עצמך: ${formAboutRef.current.value}. לינקים: ${formLinksRef.current?.value || ''}`

    try {
      await sendAffiliateEmail(data)
      setAffiliateModalMessage('המייל נשלח בהצלחה! תודה. נחזור אלייך בהקדם')
      setIsAffiliateModalOpen(true)
      formAboutRef.current.value = ''
      if (formLinksRef.current) formLinksRef.current.value = ''
    } catch {
      setAffiliateModalMessage('שגיאה. אנא נסי שנית.')
      setIsAffiliateModalOpen(true)
    }
  }

  const getPaymentsByType = (type: PaymentTypeEnum) => {
    return refUserData?.paymentsHistory?.filter(payment => payment.type === type) || []
  }

  const calculateSum = (paymentsArray: Payment[]) => {
    return paymentsArray.reduce((sum, payment) => sum + +payment.payment, 0)
  }
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('he-IL')
  }
  const totalPayments = useMemo(() => {
    return (
      refUserData?.paymentsHistory?.filter(p => p.type !== PaymentTypeEnum.Giftcard).reduce((sum, p) => sum + parseFloat(p.payment), 0) || 0
    )
  }, [refUserData])

  const totalEarningsCoupons =
    coupons?.reduce((sum, coupon) => {
      return sum + Math.round(parseFloat(coupon.revenue) * coupon.totalFinalPayments)
    }, 0) || 0

  const totalEarningsShipments = Math.round(refUserData?.packagesShipmentIncome || 0 * (refUserData?.rev || 0))

  const remainingPayments = totalEarningsCoupons + totalEarningsShipments - totalPayments

  const totalCouponUsage = useMemo(() => {
    return coupons?.reduce((acc, coupon) => acc + coupon.timesUsed, 0) || 0
  }, [coupons])
  const totalRevenue = totalEarningsCoupons + totalEarningsShipments

  if (refNum && isLoadingAffiliateUserData) {
    return (
      <Card>
        <CardContent>
          <Spinner className='mx-auto size-10' />
        </CardContent>
      </Card>
    )
  }

  if (refNum && refUserData?.refUserId) {
    return (
      <>
        <div className='space-y-4 md:space-y-6'>
          {/* Referral Link Section */}
          {!refUserData.isOnHold && (
            <Card className='bg-white'>
              <CardHeader>
                <CardTitle className='text-text-primary text-xl font-medium'>הלינק שלך</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='flex items-center gap-2'>
                  <CopyTextButton tooltipText='הועתק' value={`shiptanbul.com/?ref=${refNum}`} />
                  <div className='flex-1 rounded-lg bg-[#FAFAFA] p-3 text-end text-sm'>shiptanbul.com/?ref={refNum}</div>
                </div>

                <div className='text-text-secondary grid grid-cols-1 gap-4 md:grid-cols-3'>
                  <div className='bg-button-primary-selected-segment rounded-lg p-4'>
                    <div className='mb-1 flex items-center justify-between gap-2 text-sm'>
                      משתמשים שנרשמו
                      <UsersIcon className='size-4' />
                    </div>
                    <div className='text-xl font-medium'>{refUserData.registeredUsers || 0}</div>
                  </div>
                  <div className='bg-button-primary-selected-segment rounded-lg p-4'>
                    <div className='mb-1 flex items-center justify-between gap-2 text-sm'>
                      שימוש בקודי קופון
                      <CouponIcon className='size-4' />
                    </div>
                    <div className='text-xl font-medium'>{totalCouponUsage}</div>
                  </div>
                  <div className='bg-button-primary-selected-segment rounded-lg p-4'>
                    <div className='mb-1 flex items-center justify-between gap-2 text-sm'>
                      הרווח שלך עד כה
                      <ProfitIcon className='size-4' />
                    </div>
                    <div className='text-xl font-medium'>{totalRevenue}₪</div>
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <div className='border-border-default rounded-lg border bg-white p-4'>
                    <div className='text-text-secondary mb-1 text-sm'>שולם</div>
                    <div className='text-text-primary text-xl font-medium'>{totalPayments}₪</div>
                  </div>
                  <div className='border-border-default rounded-lg border bg-white p-4'>
                    <div className='text-text-secondary mb-1 text-sm'>נותר לתשלום</div>
                    <div className='text-text-primary text-xl font-medium'>{remainingPayments}₪</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Coupons Section */}
          <AffiliateCouponSection coupons={coupons!} />

          {/* Payment History */}
          {refUserData.paymentsHistory && refUserData.paymentsHistory.length > 0 && (
            <Card className='bg-white'>
              <CardHeader>
                <CardTitle className='text-text-primary text-xl font-medium'>פירוט תשלומים</CardTitle>
              </CardHeader>
              <CardContent className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                {Object.values(PaymentTypeEnum).map(type => {
                  const payments = getPaymentsByType(type)
                  const sum = calculateSum(payments)
                  const title = paymentTypeMap[type].label
                  const color = paymentTypeMap[type].color
                  const Icon = paymentTypeMap[type].Icon
                  return (
                    <div
                      key={type}
                      onClick={() => setSelectedPaymentType(type)}
                      className='text-text-secondary cursor-pointer space-y-2 rounded-lg p-4'
                      style={{ backgroundColor: color }}
                    >
                      <div className='flex items-center justify-between gap-2'>
                        <div className='font-medium'>{title}</div>
                        <Icon className='size-4' />
                      </div>
                      <div>{sum}₪</div>
                      <Button variant='ghost' className='h-fit p-0 hover:bg-transparent' size='sm'>
                        לפירוט תשלומים
                        <ArrowLeft />
                      </Button>
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          )}

          {refUserData.paymentsHistory && refUserData.paymentsHistory.length > 0 && (
            <Card className='bg-white'>
              <CardHeader>
                <CardTitle className='text-text-primary text-xl font-medium'>היסטוריית תשלומים</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='border-border-default overflow-hidden rounded-lg border px-3'>
                  <div className='border-border-default text-text-secondary font-sm grid grid-cols-3 gap-4 border-b py-3 font-medium'>
                    <div>סטטוס</div>
                    <div className='text-center'>סכום</div>
                    <div className='text-end'>תאריך</div>
                  </div>
                  {refUserData.paymentsHistory.map((payment, idx) => {
                    const Icon = paymentTypeMap[payment.type].Icon
                    return (
                      <div key={idx} className='border-border-default grid grid-cols-3 gap-4 border-b py-3 last:border-0'>
                        <div className='text-text-primary flex items-center gap-2 text-sm'>
                          <Icon className='size-5' style={{ color: paymentTypeMap[payment.type].iconColor }} />
                          שולם
                        </div>
                        <div className='text-text-primary text-center text-sm'>{payment.payment}₪</div>
                        <div className='text-text-primary text-end text-sm'>{formatDate(payment.date)}</div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Details Dialog */}
          {selectedPaymentType && (
            <PaymentDetailsDialog
              paymentType={selectedPaymentType}
              payments={getPaymentsByType(selectedPaymentType)}
              isOpen={!!selectedPaymentType}
              onOpenChange={open => !open && setSelectedPaymentType(null)}
            />
          )}
        </div>

        {/* Affiliate Modal */}
        {isAffiliateModalOpen && (
          <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50' dir='rtl'>
            <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
              <h3 className='mb-4 text-xl font-medium'>שים לב!</h3>
              <p className='text-text-secondary mb-4'>{affiliateModalMessage}</p>
              <Button onClick={() => setIsAffiliateModalOpen(false)} className='w-full'>
                סגור
              </Button>
            </div>
          </div>
        )}
      </>
    )
  }

  // No affiliate account - show application form
  return (
    <>
      <Card className='bg-white'>
        <CardHeader>
          <CardTitle className='text-text-primary text-xl font-medium'>שיווק שותפים</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='text-text-primary py-4 text-center'>
            מעוניינת להיות שותפה להצלחה 🚀 שלנו?
            <br />
            מלאי את הפרטים ונחזור אליך בימים הקרובים 😇
          </div>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='about'>ספרי לנו על עצמך:</Label>
              <textarea
                ref={formAboutRef}
                id='about'
                rows={4}
                className='border-border-default w-full rounded-lg border p-3 text-sm'
                dir='rtl'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='links'>לינקים לעמודים שלך ברשתות החברתיות:</Label>
              <textarea
                ref={formLinksRef}
                id='links'
                rows={4}
                className='border-border-default w-full rounded-lg border p-3 text-sm'
                dir='rtl'
              />
            </div>
            <Button onClick={handleAffiliateSubmit} disabled={isSendingEmail} className='w-full'>
              {isSendingEmail ? 'שולח...' : 'שלחי'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Affiliate Modal */}
      {isAffiliateModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50' dir='rtl'>
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-xl font-medium'>שים לב!</h3>
            <p className='text-text-secondary mb-4'>{affiliateModalMessage}</p>
            <Button onClick={() => setIsAffiliateModalOpen(false)} className='w-full'>
              סגור
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
